/**
 * FilesTab - Display matched files for the view
 *
 * Shows list of files that match view rules with metadata.
 */

import React, { useMemo } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { useViewFiles } from '@/hooks/useViews';
import type { ViewFileItem } from '@/types/views';

interface FilesTabProps {
  viewId: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * FilesTab Component
 */
export const FilesTab: React.FC<FilesTabProps> = ({ viewId }) => {
  const { data, isLoading, error } = useViewFiles(viewId);
  const files = data?.files ?? [];

  const columns = useMemo<ColumnDef<ViewFileItem>[]>(
    () => [
      {
        accessorKey: 'file_path',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>File Path</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.file_path;
          if (!value) return null;
          const parts = value.split('/');
          const fileName = parts[parts.length - 1];
          return (
            <div className="py-1">
              <div className="font-medium text-sm">{fileName}</div>
              <div className="text-xs text-muted-foreground truncate">{value}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'file_size_bytes',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>File Size</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.file_size_bytes;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return formatBytes(value);
        },
        sortingFn: (a, b) =>
          (a.original.file_size_bytes ?? 0) - (b.original.file_size_bytes ?? 0),
      },
      {
        accessorKey: 'row_count',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Row Count</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.row_count;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return value.toLocaleString();
        },
        sortingFn: (a, b) => (a.original.row_count ?? 0) - (b.original.row_count ?? 0),
      },
      {
        accessorKey: 'dataset_name',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Dataset</DataTableSortHeader>
        ),
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Created</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.created_at;
          if (!value) return <span className="text-muted-foreground">-</span>;
          try {
            return new Date(value).toLocaleString();
          } catch {
            return value;
          }
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Loading files...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load files for this view</AlertDescription>
      </Alert>
    );
  }

  if (files.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No Files Matched</p>
            <p className="text-sm">
              This view currently has no files matching its rules. Try refreshing the view or
              adjusting the rules.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Files Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Matched Files</CardTitle>
          <CardDescription>
            {data?.total_count ?? files.length} files matched by view rules
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Files DataTable */}
      <Card>
        <CardContent className="pt-6">
          <DataTable
            data={files}
            columns={columns}
            pageSize={50}
            initialSorting={[{ id: 'created_at', desc: true }]}
            getRowId={(f) => f.file_path}
          />
        </CardContent>
      </Card>
    </div>
  );
};
